import axiosClient from '@/api/axiosClient';
import type { QueryColumn } from '@/api/contracts/queryResult';
import type { DataViewSelectionContext, FreeTextSearch } from '@/api/contracts/dataModel';

export interface TableQueryRequest {
  page?: number;
  pageSize?: number;
  additionalFilters?: Array<{
    tableAliasOrName: string;
    columnName: string;
    operator: string;
    value?: string | null;
  }>;
  orderByOverride?: Array<{
    tableAliasOrName: string;
    columnName: string;
    ascending: boolean;
  }>;
  freeText?: FreeTextSearch | null;
}

export interface TableQueryResponse {
  columns: QueryColumn[];
  rows: Record<string, unknown>[];
  page: number;
  pageSize: number;
  total: number;
  tableKey: string;
}

// US 3536: a mass operation names its rows either explicitly or by a selection the client never
// materialized. Exactly one of the two is sent - the service refuses a request that answers
// "which rows" twice, because there is no safe way to pick between two answers.
export interface UpdateTableCellRequest {
  rowIds?: number[];
  selection?: DataViewSelectionContext;
  columnKey: string;
  newValue: unknown;
  operation?: 'replace' | 'appendComment';
}

export interface UpdateTableCellResponse {
  updatedRows: number;
}

export interface AddTableRowsRequest {
  rows: Record<string, unknown>[];
  fieldActions?: Record<string, string>;
}

// WI 2184: id-ene til de nyopprettede radene (ICSRowId_), i samme rekkefølge som request.rows.
// Tom hvis tabellen ikke har ICSRowId_-identitetskolonne.
export interface AddTableRowsResponse {
  insertedRows: number;
  createdRowIds: number[];
}

export interface CopyTableRowsRequest {
  sourceRowIds: number[];
  fieldActions?: Record<string, string>;
  fieldValues?: Record<string, unknown>;
}

export interface CopyTableRowsResponse {
  insertedRows: number;
}

/** US 3536: exactly one of rowIds and selection. See {@link UpdateTableCellRequest}. */
export interface DeleteTableRowsRequest {
  rowIds?: number[];
  selection?: DataViewSelectionContext;
}

export interface DeleteTableRowsResponse {
  deletedRows: number;
}

/** US 3536: exactly one of rowIds and selection. See {@link UpdateTableCellRequest}. */
export interface RestoreTableRowsRequest {
  rowIds?: number[];
  selection?: DataViewSelectionContext;
}

export interface RestoreTableRowsResponse {
  restoredRows: number;
}

// Task 2351: resultat av eksplisitt «Regenerer løpenummer» på en eksisterende tabell.
export interface RegenerateSequenceResponse {
  assignedRows: number;
}

export async function updateTableCell (
  tableKeyOrId: string | number,
  body: UpdateTableCellRequest
): Promise<UpdateTableCellResponse> {
  const tableRef = String(tableKeyOrId ?? '').trim();
  const { data } = await axiosClient.patch<UpdateTableCellResponse>(
    `v2/table/${tableRef}/rows/cell`,
    body
  );
  return data;
}

export async function addTableRows (
  tableKeyOrId: string | number,
  body: AddTableRowsRequest
): Promise<AddTableRowsResponse> {
  const tableRef = String(tableKeyOrId ?? '').trim();
  const { data } = await axiosClient.post<AddTableRowsResponse>(`v2/table/${tableRef}/rows`, body);
  return data;
}

export async function getTableRow (
  tableKeyOrId: string | number,
  rowId: number
): Promise<Record<string, unknown> | null> {
  const tableRef = String(tableKeyOrId ?? '').trim();
  const { data } = await axiosClient.get<Record<string, unknown> | null>(
    `v2/table/${tableRef}/rows/${rowId}`
  );
  return data ?? null;
}

export async function copyTableRows (
  tableKeyOrId: string | number,
  body: CopyTableRowsRequest
): Promise<CopyTableRowsResponse> {
  const tableRef = String(tableKeyOrId ?? '').trim();
  const { data } = await axiosClient.post<CopyTableRowsResponse>(
    `v2/table/${tableRef}/rows/copy`,
    body
  );
  return data;
}

export async function deleteTableRows (
  tableKeyOrId: string | number,
  body: DeleteTableRowsRequest
): Promise<DeleteTableRowsResponse> {
  const tableRef = String(tableKeyOrId ?? '').trim();
  const { data } = await axiosClient.delete<DeleteTableRowsResponse>(
    `v2/table/${tableRef}/rows`,
    { data: body }
  );
  return data;
}

export async function restoreTableRows (
  tableKeyOrId: string | number,
  body: RestoreTableRowsRequest
): Promise<RestoreTableRowsResponse> {
  const tableRef = String(tableKeyOrId ?? '').trim();
  const { data } = await axiosClient.post<RestoreTableRowsResponse>(
    `v2/table/${tableRef}/rows/restore`,
    body
  );
  return data;
}

// Task 2351: eksplisitt «Regenerer løpenummer» — fyller blanke løpenummer-celler på tabellens
// egne rader (faktiske verdier bevares). Ingen request-body; tabellens config styrer tildelingen.
export async function regenerateTableSequence (
  tableKeyOrId: string | number
): Promise<RegenerateSequenceResponse> {
  const tableRef = String(tableKeyOrId ?? '').trim();
  const { data } = await axiosClient.post<RegenerateSequenceResponse>(
    `v2/table/${tableRef}/regenerate-sequence`
  );
  return data;
}

// US 3596: the backend owned rules for copying rows. The client reads these before it offers
// the copy action, so the limit and the wording exist in exactly one place.
export interface CopyRowsConstraints {
  maxRows: number;
  tooManyRowsMessage: string;
}

export async function fetchCopyRowsConstraints (
  signal?: AbortSignal
): Promise<CopyRowsConstraints> {
  const { data } = await axiosClient.get<CopyRowsConstraints>('v2/table/copy-constraints', {
    signal
  });
  return data;
}
